import { FC, ReactNode, useEffect } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import { getLanguages, Language, Subsidiary } from "../../http/subsidiaries/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";

export interface SubsidiaryFormBody {
  name: string;
  allowMultiPlates: boolean;
  vatDeductionCode?: string;
  vatServiceCode?: string;
  countryIsoCodeAlpha2?: string;
  defaultLanguage?: Language;
  semiramisOrganizationCode: string;
}

type SubsidiaryFormProps = {
  subsidiary?: Subsidiary | null;
  onSubmit: (form: SubsidiaryFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  secondaryButton?: ReactNode;
};

const SubsidiaryForm: FC<SubsidiaryFormProps> = ({
  subsidiary,
  onSubmit,
  isDisabled,
  isLoading,
  secondaryButton,
}: SubsidiaryFormProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();

  const languages = getLanguages(i18n.t);

  const defaultLanguage = languages.find((x) => x.value === subsidiary?.defaultLanguage);

  const defaultValues = {
    name: subsidiary?.name,
    allowMultiPlates: subsidiary?.allowMultiPlates,
    vatDeductionCode: subsidiary?.vatDeductionCode,
    vatServiceCode: subsidiary?.vatServiceCode,
    countryIsoCodeAlpha2: subsidiary?.countryIsoCodeAlpha2,
    defaultLanguage: defaultLanguage,
    semiramisOrganizationCode: subsidiary?.semiramisOrganizationCode,
  };

  return (
    <Form<SubsidiaryFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => (formData) => onSubmit(formData)}
      itemCreationInfo={subsidiary}
      secondaryButton={secondaryButton}
    >
      {({ register, control, formState: { errors }, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [subsidiary?.id]);

        const allowMultiPlates = watch("allowMultiPlates");

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.allowCarsToHaveMultiplePlates} error={errors.allowMultiPlates} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={allowMultiPlates}
                  disabled={isDisabled || isLoading}
                  onChange={(checked) => setValue("allowMultiPlates", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.countryIsoCode} error={errors.countryIsoCodeAlpha2} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  maxLength={2}
                  disabled={isDisabled || isLoading}
                  forceUpper
                  {...register("countryIsoCodeAlpha2")}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.vatDeductionCode} error={errors.vatDeductionCode} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={
                    !userSettings.permissions.subsidiaryVatCodes.insert ||
                    !userSettings.permissions.subsidiaryVatCodes.update ||
                    isDisabled ||
                    isLoading
                  }
                  forceUpper
                  {...register("vatDeductionCode")}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.vatServiceCode} error={errors.vatServiceCode} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={
                    !userSettings.permissions.subsidiaryVatCodes.insert ||
                    !userSettings.permissions.subsidiaryVatCodes.update ||
                    isDisabled ||
                    isLoading
                  }
                  forceUpper
                  {...register("vatServiceCode")}
                />
              )}
            </FormField>
            <Controller
              name="defaultLanguage"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.language} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Select
                      {...field}
                      inputId={labelId}
                      options={languages}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isClearable
                      styles={reactSelectStyles<Language>(isInvalid)}
                      menuPosition="fixed"
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.t.common.semiramisOrganizationCode} error={errors.semiramisOrganizationCode}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  forceUpper
                  {...register("semiramisOrganizationCode", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SubsidiaryForm;
